import * as Sentry from '@sentry/react';
import type { ZodIssue } from 'zod';

import {
  GRAPHQL_ERROR_STATUS,
  SCHEMA_MISMATCH_STATUS,
  TGraphQLResponse,
  TQueryResult,
} from '@/shared/redux/api/base-api.types';
import { normalizeGraphQLData } from '@/shared/redux/api/utils/normalize-graphql-data';
import { getOperationValidator } from '@/shared/redux/api/utils/operation-validators';

// Per-session dedupe: a polling/refetching query that hits persistent backend
// drift would otherwise emit the same Sentry event hundreds of times per
// hour. Key on (operation, first issue code, first issue path) so distinct
// mismatches still fire, but the same shape across repeated requests
// collapses to one event. State is module-scoped; a page reload resets it,
// which is the right granularity for a SPA.
const reportedMismatches = new Set<string>();
const reportedMissingValidators = new Set<string>();

const dedupeKey = (
  operationName: string,
  issues: readonly ZodIssue[],
): string => {
  const first = issues[0];
  const code = first?.code ?? 'unknown';
  const path = first?.path?.join('.') ?? '';
  return `${operationName}|${code}|${path}`;
};

const reportSchemaMismatch = (
  operationName: string,
  issues: readonly ZodIssue[],
) => {
  const key = dedupeKey(operationName, issues);
  if (reportedMismatches.has(key)) return;
  reportedMismatches.add(key);

  // Log to the browser console so developers can spot drift during local dev
  // and QA without needing a Sentry session. Do not report response payloads:
  // GraphQL responses can contain secrets or user data.
  console.error('GraphQL schema mismatch', {
    operationName,
    issues,
  });
  Sentry.captureMessage('GraphQL schema mismatch', {
    level: 'error',
    extra: { operationName, issues },
  });
};

const reportMissingValidator = (operationName: string) => {
  if (reportedMissingValidators.has(operationName)) return;
  reportedMissingValidators.add(operationName);

  console.error('GraphQL schema validator missing', { operationName });
  Sentry.captureMessage('GraphQL schema validator missing', {
    level: 'warning',
    extra: { operationName },
  });
};

// Exported for tests only: per-session dedupe state would otherwise leak
// across describe/it boundaries and silently mask assertions.
export const __resetSchemaMismatchDedupeForTests = () => {
  reportedMismatches.clear();
  reportedMissingValidators.clear();
};

/**
 * Processes the response and converts it to a TQueryResult.
 *
 * When an operation has a registered Zod validator, the raw response data is
 * validated against it BEFORE the GraphQL-errors branch — otherwise a
 * malformed partial-data payload could be smuggled past validation as a
 * GRAPHQL_ERROR and then recovered as `data` by handleError. On mismatch,
 * we surface a typed SCHEMA_MISMATCH error (so callers see `error`, not a
 * half-broken `data`) and report validation issues to Sentry without
 * sending the response payload. Returned GraphQL data is normalized after
 * validation so null values follow the UI/form contract without hiding
 * schema drift.
 */
export const processResponse = (
  response: Response,
  jsonResult: TGraphQLResponse,
  operationName?: string,
): TQueryResult => {
  if (!response.ok) {
    return {
      error: {
        status: response.status,
        data: jsonResult,
        message: jsonResult?.message || `HTTP ${response.status}`,
      },
    };
  }

  const data = jsonResult?.data;
  const validator = operationName
    ? getOperationValidator(operationName)
    : undefined;
  if (operationName && !validator) {
    reportMissingValidator(operationName);
  }
  const normalizeData = () =>
    data === undefined ? undefined : normalizeGraphQLData(data, validator);

  const hasErrors = Boolean(jsonResult?.errors?.length);
  const dataIsPresent = data !== undefined && data !== null;

  // Validate registered operations whenever there is data to check, or when
  // the operation was supposed to produce data and didn't (no errors to
  // explain its absence). Skipping validation only when errors AND no data
  // are present preserves the standard "auth/validation failure → empty
  // data + errors" GraphQL response shape as a plain GRAPHQL_ERROR. Any
  // other shape — including errors with malformed partial data — must
  // fail the schema gate so handleError cannot recover invalid payloads
  // back into a UI-facing `data` field.
  if (validator && (dataIsPresent || !hasErrors)) {
    const parsed = validator.safeParse(data);
    if (!parsed.success) {
      reportSchemaMismatch(operationName!, parsed.error.issues);
      return {
        error: {
          status: SCHEMA_MISMATCH_STATUS,
          data: normalizeData(),
          message: `Response did not match schema for ${operationName}`,
        },
      };
    }
  }

  if (hasErrors) {
    return {
      error: {
        status: GRAPHQL_ERROR_STATUS,
        data: normalizeData(),
        message: jsonResult?.errors?.[0]?.message || 'GraphQL Error',
        extensions: jsonResult?.errors?.[0]?.extensions,
      },
    };
  }

  return { data: normalizeData() };
};
